import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Container,
  Image,
} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import axios from "axios";
import {
  Eye,
  Pencil,
  Trash,
  Battery,
  PinMap,
  Calendar,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Baseurl } from "../../baseurl";

const VehicleList = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Baseurl}/api/vehicles/allTypeVehicles`
      );
      setVehicles(response.data?.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load vehicles");
      setLoading(false);
    }
  };

  const handleView = (vehicle) => {
    navigate(`/auctions/get/${vehicle._id}`);
  };

  const handleEdit = (vehicle) => {
    navigate(`/auctions/editAuction/${vehicle._id}`);
  };

  const handleDelete = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${Baseurl}/api/vehicles/${selectedVehicle._id}`);
      setVehicles(vehicles.filter((v) => v._id !== selectedVehicle._id));
      setShowDeleteModal(false);
      setSelectedVehicle(null);
      toast.success("Vehicle deactivated successfully");
    } catch (err) {
      toast.error("Failed to delete vehicle");
    }
  };

  const getAuctionStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">Draft</Badge>;
    
    const variants = {
      Live: "success",
      Upcoming: "warning",
      Ended: "secondary",
      Sold: "info",
      Unsold: "danger",
      Draft: "secondary",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const formatPrice = (price) => {
    if (!price) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFeatured = async (vehicle) => {
    try {
      if (vehicle.flags?.isActive && vehicle.auction?.status === "Live") {
        const config = {
          headers: {
            "Content-Type": "application/json",
          },
        };
        const url = `${Baseurl}/api/vehicles/makeVehicleFeatured/${vehicle._id}`;
        const resp = await axios.put(url, {}, config);
        if (resp.data.success) {
          // Refresh the list after updating
          fetchVehicles();
          toast.success("Vehicle marked as featured");
        }
      } else {
        toast.warn(
          "The vehicle should be active and in Live auction status to be featured"
        );
      }
    } catch (error) {
      toast.error("Something went wrong");
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading vehicles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  if (vehicles.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        No vehicles found.
      </Alert>
    );
  }

  return (
    <Container fluid className="py-4 px-5">
      <Row className="mb-4">
        <Col>
          <h1 className="h2">Auction Vehicle List</h1>
          <p className="text-muted">Manage vehicles in the auction platform</p>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table striped bordered hover className="align-middle">
          <thead className="bg-light">
            <tr>
              <th width="80px">Image</th>
              <th>Vehicle Details</th>
              <th>Specifications</th>
              <th>Auction Status</th>
              <th>Featured</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => (
              <tr key={vehicle._id}>
                {/* Image Column */}
                <td className="d-flex justify-content-center align-items-center">
                  <Image
                    src={
                      vehicle.media?.images?.[0] ||
                      "https://via.placeholder.com/60x40?text=No+Image"
                    }
                    alt={vehicle.title}
                    rounded
                    style={{
                      width: "60px",
                      height: "40px",
                      objectFit: "cover",
                    }}
                  />
                </td>

                {/* Vehicle Details Column */}
                <td className="px-2">
                  <div>
                    <strong>{vehicle.title}</strong>
                    <div className="text-muted small">
                      {vehicle.brand} • {vehicle.model} • {vehicle.variant}
                    </div>
                    <div className="mt-1">
                      <small className="text-muted">
                        <PinMap size={12} className="me-1" />
                        {vehicle.yard?.parkedAt || "N/A"}
                      </small>
                    </div>
                  </div>
                </td>

                {/* Specifications Column */}
                <td className="px-2">
                  <div>
                    {vehicle.evSpecs?.batteryCapacity ? (
                      <div className="mb-1">
                        <Battery size={14} className="me-1 text-success" />
                        <small>
                          {vehicle.evSpecs.batteryCapacity.value}{" "}
                          {vehicle.evSpecs.batteryCapacity.unit} •{" "}
                          {vehicle.evSpecs.batteryHealthPercent || 0}% health
                        </small>
                      </div>
                    ) : (
                      <div className="mb-1">
                        <Calendar size={14} className="me-1" />
                        <small>
                          {vehicle.manufacturing?.year || "N/A"}
                        </small>
                      </div>
                    )}
                    <div>
                      <small className="text-muted">
                        {vehicle.identifiers?.bodyType || "N/A"} •{" "}
                        {vehicle.specifications?.cubicCapacity || "N/A"} CC •{" "}
                        {vehicle.color || "N/A"}
                      </small>
                    </div>
                  </div>
                </td>

                {/* Auction Status Column */}
                <td className="px-2">
                  <div className="d-flex flex-column">
                    {getAuctionStatusBadge(vehicle.auction?.status)}
                    {vehicle.pricing?.currentBid > 0 && (
                      <small className="text-danger mt-1 fw-bold align-center">
                        Current bid: {formatPrice(vehicle.pricing.currentBid)}
                      </small>
                    )}
                    <small className="text-muted mt-1">
                      Bids: {vehicle.stats?.bids || 0}
                    </small>
                  </div>
                </td>

                {/* Featured Column */}
                <td className="px-2">
                  <div className="fw-bold" style={{ color: "#304457" }}>
                    <Form.Check
                      type="switch"
                      id={`feature-switch-${vehicle._id}`}
                      checked={vehicle.flags?.isFeatured || false}
                      onChange={() => handleFeatured(vehicle)}
                    />
                  </div>
                </td>

                {/* Price Column */}
                <td className="px-2">
                  <div className="fw-bold" style={{ color: "#304457" }}>
                    {formatPrice(vehicle.pricing?.basePrice)}
                    {vehicle.pricing?.reservePrice && (
                      <div className="text-muted small">
                        Reserve: {formatPrice(vehicle.pricing.reservePrice)}
                      </div>
                    )}
                  </div>
                </td>

                {/* Actions Column */}
                <td className="px-2">
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleView(vehicle)}
                      title="View"
                    >
                      <Eye />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleEdit(vehicle)}
                      title="Edit"
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(vehicle)}
                      title="Delete"
                      disabled={!vehicle.flags?.isActive}
                    >
                      <Trash />
                    </Button>
                  </div>
                  <div className="mt-1 small">
                    {!vehicle.flags?.isActive && (
                      <span className="text-danger">Deactivated</span>
                    )}
                    {vehicle.flags?.isVerified && (
                      <Badge bg="success" className="ms-1">Verified</Badge>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate vehicle{" "}
          <strong>{selectedVehicle?.title}</strong>? 
          <br />
          <small className="text-muted">
            (This will mark the vehicle as inactive instead of permanent deletion)
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Deactivate
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VehicleList;